import { COSMETICS, cosmetic, styleSuffix, type Look } from '../../shared/cosmetics.ts';
import { PersonAvatar } from './Brand.tsx';

interface AvatarProps {
  look: Look | null;
  /** Used for the initial when a guest has no avatar. */
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/** A player's avatar inside their frame. Guests (no look) show their initial. */
export function PlayerAvatar({ look, name, size = 'md' }: AvatarProps) {
  const avatar = cosmetic(look?.avatar);
  const frame = look?.frame ? styleSuffix(look.frame) : null;
  const className = [
    'look-avatar',
    `size-${size}`,
    avatar?.tone && `tone-${avatar.tone}`,
    avatar && `av-${avatar.rarity}`,
    frame && `framed frame-${frame}`,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className} aria-hidden="true">
      <span className="look-avatar-inner">
        {avatar?.emoji ? <span className="emoji">{avatar.emoji}</span> : <PersonAvatar name={name} />}
      </span>
      {frame && <span className="frame-fx" />}
    </span>
  );
}
/** A player's name in their chosen name style (with its flair emoji, if any). */
export function PlayerName({ name, look }: { name: string; look: Look | null }) {
  const style = cosmetic(look?.nameStyle);
  if (!style) return <span className="player-name-text">{name}</span>;
  return (
    <span className={`player-name-text styled-name name-${styleSuffix(style.id)}`}>
      {style.flair && <span className="name-flair emoji" aria-hidden="true">{style.flair}</span>}
      <span className="styled-name-text">{name}</span>
      {style.flair && <span className="name-flair emoji" aria-hidden="true">{style.flair}</span>}
    </span>
  );
}

export function GoldAmount({ amount, signed }: { amount: number; signed?: boolean }) {
  return (
    <span className="gold-amount">
      <span className="emoji" aria-hidden="true">
        🪙
      </span>
      {signed && amount > 0 ? '+' : ''}
      {amount.toLocaleString()}
    </span>
  );
}

/** How an item looks when worn: an avatar, a frame around your avatar, or your styled name. */
export function CosmeticPreview({
  id,
  name,
  look,
  size = 'md',
}: {
  id: string;
  name: string;
  look: Look;
  size?: AvatarProps['size'];
}) {
  const item = COSMETICS.find((c) => c.id === id);
  if (!item) return null;
  if (item.kind === 'avatar') return <PlayerAvatar look={{ ...look, avatar: id, frame: null }} name={name} size={size} />;
  if (item.kind === 'frame') return <PlayerAvatar look={{ ...look, frame: id }} name={name} size={size} />;
  return <PlayerName name={name} look={{ ...look, nameStyle: id }} />;
}
